/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Input, Layout, message, Modal, Row, Typography } from 'antd';
import { get, identity, startCase } from 'lodash';

import PdButton from 'components/pd-button';
import { RiskCreateEdit } from './create-edit';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';
import RiskManagementService from 'services/risk-management';

import { errHandling } from 'utils';
import queryString from 'query-string';

import './style.scss';

const { Content } = Layout;
const { Search } = Input;
const { confirm } = Modal;
const { Text } = Typography;

export function RiskManagement() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [loadStatus, setLoadStatus] = useState(true);
  const [modalContent, setModalContent] = useState({});
  const [nextPage, setNextPage] = useState(-1);
  const [searchValue, setSearchValue] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [riskList, setRiskList] = useState([]);

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);

  const riskStatus = ({ status } = {}) => (
    <div className="status-indicator">
      <Text className={status}>{startCase(status)}</Text>
    </div>
  );

  const buttonAction = (row = {}) => {
    const { id, status } = row;
    return (
      <Row className="button-table">
        {status === 'active' ? (
          <>
            <Col span={12} className="button">
              <PdButton
                className="height-32"
                type="danger"
                outline
                full
                onClick={handleDeactivateRisk.bind(this, id)}>
                Deactivate
              </PdButton>
            </Col>
            <Col span={12} className="button">
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={handleEditRisk.bind(this, row)}>
                Edit
              </PdButton>
            </Col>
          </>
        ) : (
          <>
            <Col span={12} className="button">
              <PdButton
                className="height-32"
                type="primary"
                outline
                full
                onClick={handleActivateRisk.bind(this, id)}>
                Reactivate
              </PdButton>
            </Col>
            <Col span={12} className="button">
              <PdButton
                className="height-32"
                type="danger"
                full
                onClick={handleDeleteRisk.bind(this, id)}
                outline>
                Delete
              </PdButton>
            </Col>
          </>
        )}
      </Row>
    );
  };

  const columns = [
    {
      name: 'Risk Level',
      selector: row => row.title,
      width: '38%',
      center: true,
    },
    {
      name: 'Status',
      cell: riskStatus,
      width: '37%',
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '25%',
    },
  ];

  function handleDeactivateRisk(riskId) {
    confirm({
      title: 'Are you sure deactivate this risk?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await RiskManagementService.deactivateRisk(riskId);
          fetchRisk();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  async function handleActivateRisk(riskId) {
    setLoadStatus(true);
    try {
      await RiskManagementService.reactivateRisk(riskId);
      fetchRisk();
      message.success('Successfully restore the risk');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function handleDeleteRisk(riskId) {
    confirm({
      title: 'Are you sure want to delete this risk?',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      async onOk() {
        setLoadStatus(true);
        try {
          await RiskManagementService.deleteRisk(riskId);
          fetchRisk();
        } catch (error) {
          errHandling(error);
        } finally {
          setLoadStatus(false);
        }
      },
    });
  }

  function handleEditRisk(risk) {
    setModalContent(risk);
    setShowModal(true);
  }

  async function fetchRisk({ page, q } = {}) {
    setLoadStatus(true);
    try {
      const { data } = await RiskManagementService.getRiskList({ q });
      setRiskList(data.data);

      // setNextPage(cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setShowModal(false);
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/risk_managements',
      search: stringified,
    });
  }

  function filterRisk(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/risk_managements',
      search: stringified,
    });
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    fetchRisk({
      page: parsed['page'],
      q: parsed['q'],
    });
    handleCloseModal();
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/risk_managements',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['q']) setSearchValue(parsed['q']);
    else setSearchValue('');

    fetchRisk({
      page: parsed['page'],
      q: parsed['q'],
    });
  }, [parsed['page'], parsed['q']]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-risk-management">
      <div className="content">
        <div className="pageTitle">Risk Management</div>
        <Content className="pageContent">
          <Row
            gutter={[18, 36]}
            className="pd-margin-bottom-md"
            type="flex"
            align="middle">
            <Col lg={14}>
              <Search
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                enterButton
                className="search-product"
                placeholder="Search Risk"
                onSearch={filterRisk.bind(this, 'q')}
              />
            </Col>
            <Col lg={{ span: 4, offset: 5 }}>
              <PdButton
                className="height-32"
                type="primary"
                full
                onClick={setShowModal.bind(this, true)}>
                Add Risk
              </PdButton>
            </Col>
          </Row>

          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={riskList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <RiskCreateEdit
            closeModal={handleCloseCreateEdit}
            risk={modalContent}
            warehouseList={[]}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default { RiskManagement };
